import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TfiLocationPin } from "react-icons/tfi";
import ApiService from "../nhrc_apis/ApiService";

const { JobFairAPI } = ApiService;

function JfTotalApplications() {
    const [page, setPage] = useState(1);
    const pageSize = 4;
    const navigate = useNavigate();

    const [jobs, setJobs] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [appCounts, setAppCounts] = useState({});

    useEffect(() => {
        fetchJobFairs();
    }, []);

    /* =========================
       FETCH JOB FAIRS
    ========================= */
    const fetchJobFairs = async () => {
        setLoading(true);
        try {
            const response = await JobFairAPI.getAllJobFairs();

            console.log("ALL JOB FAIRS RESPONSE :", response);

            const events =
                response?.data?.data ||
                response?.data?.content ||
                response?.data?.job_fairs ||
                response?.data?.result ||
                response?.data ||
                [];

            const safeEvents = Array.isArray(events) ? events : [];
            console.log("SAFE EVENTS :", safeEvents);

            setJobs(safeEvents);
            fetchApplicationCounts(safeEvents);
        } catch (error) {
            console.error("Failed to fetch job fairs:", error);
            setJobs([]);
        } finally {
            setLoading(false);
        }
    };

    /* =========================
       FETCH COUNTS PER EVENT
       ✅ FIXED: uses exact API keys
       student_registrations & hr_registrations
    ========================= */
    const fetchApplicationCounts = async (events) => {
        const result = {};

        await Promise.all(
            events.map(async (event) => {
                try {
                    const res = await JobFairAPI.getJobFairRegistrations(event.id);

                    console.log(`RAW RESPONSE for event ${event.id} :`, res);
                    console.log(`res.data for event ${event.id} :`, res?.data);

                    const data = res?.data;

                    // ✅ API returns: { student_registrations: [...], hr_registrations: [...] }
                    const studentList = Array.isArray(data?.student_registrations)
                        ? data.student_registrations
                        : [];

                    const hrList = Array.isArray(data?.hr_registrations)
                        ? data.hr_registrations
                        : [];

                    const total = studentList.length + hrList.length;

                    result[event.id] = {
                        total,
                        students: studentList.length,
                        employees: hrList.length,
                        representatives: 0,
                    };

                    console.log(`FINAL COUNT for event ${event.id} :`, result[event.id]);

                } catch (err) {
                    console.error(`Failed to fetch registrations for event ${event.id}:`, err);
                    result[event.id] = {
                        total: 0,
                        students: 0,
                        employees: 0,
                        representatives: 0,
                    };
                }
            })
        );

        setAppCounts(result);
    };

    /* =========================
       FORMAT DATE
    ========================= */
    const formatDate = (dateStr) => {
        const date = dateStr ? new Date(dateStr) : new Date();
        const isValid = !isNaN(date.getTime());
        return (isValid ? date : new Date()).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    /* =========================
       FILTER
    ========================= */
    const filteredJobs = useMemo(() => {
        const q = searchInput.toLowerCase();
        return jobs.filter((job) => {
            const title = (job?.title || job?.event_title || job?.job_fair_title || "").toLowerCase();
            const location = (job?.location || job?.venue || job?.event_location || "").toLowerCase();
            return title.includes(q) || location.includes(q);
        });
    }, [jobs, searchInput]);

    /* =========================
       PAGINATION
    ========================= */
    const totalPages = Math.ceil(filteredJobs.length / pageSize);

    const currentJobs = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return filteredJobs.slice(startIndex, startIndex + pageSize);
    }, [filteredJobs, page]);

    /* =========================
       SELECT
    ========================= */
    const allSelected =
        currentJobs.length > 0 &&
        currentJobs.every((job) => selectedIds.includes(job.id));

    const handleToggleSelectAll = (e) => {
        const ids = currentJobs.map((j) => j.id);
        if (e.target.checked) {
            setSelectedIds((p) => [...new Set([...p, ...ids])]);
        } else {
            setSelectedIds((p) => p.filter((id) => !ids.includes(id)));
        }
    };

    const handleToggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    /* =========================
       DELETE
    ========================= */
    const handleDelete = async () => {
        if (selectedIds.length === 0) {
            alert("Please select item(s) to delete");
            return;
        }
        if (!window.confirm(`Delete ${selectedIds.length} job fair(s)?`)) return;

        try {
            if (typeof JobFairAPI.deleteJobFair !== "function") {
                alert("Delete is not supported by the API yet.");
                return;
            }
            await Promise.all(selectedIds.map((id) => JobFairAPI.deleteJobFair(id)));
            await fetchJobFairs();
            setSelectedIds([]);
            setPage(1);
        } catch (error) {
            console.error("Delete failed:", error);
            alert("Failed to delete selected job fairs. Please try again.");
        }
    };

    /* =========================
       NAV
    ========================= */
    const handlePreview = (job) => {
        navigate("/dashboard/JobsFair", {
            state: { job, returnPath: "/dashboard/JobsFair" },
        });
    };

    const handleViewResponses = (job) => {
        navigate(`/dashboard/JfTotalApplicationsRes/${job.id}`, {
            state: {
                jobTitle: job?.title || job?.event_title || job?.job_fair_title,
                jobId: job.id,
                returnPath: "/dashboard/JfTotalApplicationsRes",
            },
        });
    };

    const handleAddJob = () => {
        navigate("/dashboard/JobsFair", {
            state: { returnPath: "/dashboard/JobsFair" },
        });
    };

    /* =========================
       UI
    ========================= */
    return (
        <section className="Hrjobpost">
            <h3 className="training-title mt-1 fw-bold" style={{ color: "#8D0000" }}>
                JOBS & JOB FAIRS
            </h3>
            <div className="container mt-5">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="mb-0">All Events</h5>
                        <small className="text-muted">
                            Total: {filteredJobs.length} of {jobs.length}
                        </small>
                    </div>
                    <button
                        className="btn btn-success btn-sm d-flex align-items-center gap-1 p-2"
                        onClick={handleAddJob}
                    >
                        + Post Event
                    </button>
                </div>

                {/* ACTION BAR */}
                <div className="bg-light border rounded-3 p-3 mb-4 d-flex flex-wrap align-items-center gap-3">
                    <div className="form-check ms-1">
                        <input
                            type="checkbox"
                            style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
                            className="form-check-input"
                            id="selectAll"
                            checked={allSelected}
                            onChange={handleToggleSelectAll}
                        />
                        <label className="form-check-label ms-1" htmlFor="selectAll" style={{ color: "#333" }}>
                            Select All
                        </label>
                    </div>

                    <div className="flex-grow-1 d-flex gap-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by Job Title or Location..."
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value);
                                setPage(1);
                            }}
                        />
                        <button className="btn btn-primary px-4" onClick={() => setPage(1)}>
                            Search
                        </button>
                    </div>

                    <button className="btn btn-outline-danger" onClick={handleDelete}>
                        Delete ({selectedIds.length})
                    </button>
                </div>

                {/* LOADING */}
                {loading && (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status" />
                        <p className="mt-2 text-muted">Loading job fairs...</p>
                    </div>
                )}

                {/* JOB FAIR CARDS */}
                {!loading &&
                    currentJobs.map((job) => {
                        const count = appCounts[job.id] || {};

                        const title =
                            job?.title || job?.event_title || job?.job_fair_title || "Untitled Event";
                        const location =
                            job?.location || job?.venue || job?.event_location || "Location N/A";

                        const rawStatus = job?.status || job?.eventStatus || job?.event_status || "";
                        const isOpen =
                            String(rawStatus).toLowerCase() === "open" ||
                            job?.isActive === true ||
                            job?.is_active === true;

                        const eventDate =
                            job?.eventdate ||
                            job?.event_date ||
                            job?.eventDate ||
                            job?.startDate ||
                            job?.start_date ||
                            job?.createdAt ||
                            job?.created_at ||
                            null;

                        return (
                            <div key={job.id} className="card mb-3 shadow-sm border-0">
                                <div className="card-body">

                                    {/* TOP ROW */}
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="d-flex align-items-start gap-2">
                                            <input
                                                type="checkbox"
                                                style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
                                                className="form-check-input mt-1"
                                                checked={selectedIds.includes(job.id)}
                                                onChange={() => handleToggleSelect(job.id)}
                                            />
                                            <div>
                                                <h6 className="mb-1">{title}</h6>
                                                <small className="text-muted" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                    <TfiLocationPin />
                                                    {location}
                                                </small>
                                            </div>
                                        </div>
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() => handlePreview(job)}
                                        >
                                            Event Preview
                                        </button>
                                    </div>

                                    {/* STATS ROW — clickable buttons navigate to responses */}
                                    <div className="d-flex flex-wrap justify-content-center gap-4 my-2">
                                        <button
                                            className="btn btn-sm btn-dark d-flex flex-column align-items-center px-3 py-2"
                                            onClick={() => handleViewResponses(job)}
                                        >
                                            <span className="fw-bold fs-5">{count.total ?? 0}</span>
                                            <span className="small">Total</span>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-primary d-flex flex-column align-items-center px-3 py-2"
                                            onClick={() => handleViewResponses(job)}
                                        >
                                            <span className="fw-bold fs-5">{count.students ?? 0}</span>
                                            <span className="small">Students</span>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-success d-flex flex-column align-items-center px-3 py-2"
                                            onClick={() => handleViewResponses(job)}
                                        >
                                            <span className="fw-bold fs-5">{count.employees ?? 0}</span>
                                            <span className="small">Companies / HR</span>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-warning d-flex flex-column align-items-center px-3 py-2"
                                            onClick={() => handleViewResponses(job)}
                                        >
                                            <span className="fw-bold fs-5">{count.representatives ?? 0}</span>
                                            <span className="small">Representatives</span>
                                        </button>
                                    </div>

                                    {/* FOOTER ROW */}
                                    <div className="d-flex flex-column align-items-end mt-2 small">
                                        <span
                                            className={`badge ${isOpen ? "bg-success" : "bg-danger"}`}
                                            style={{ fontSize: "13px", marginBottom: "6px" }}
                                        >
                                            {isOpen ? "Open" : "Closed"}
                                        </span>
                                        <span className="text-muted">
                                            Posted:{" "}
                                            <span className="text-warning fw-bold">
                                                {formatDate(eventDate)}
                                            </span>
                                        </span>
                                    </div>

                                </div>
                            </div>
                        );
                    })}

                {/* EMPTY STATE */}
                {!loading && currentJobs.length === 0 && (
                    <div className="text-center py-5">
                        <h6 className="text-muted">No job fairs found</h6>
                        <p className="text-muted small">Try adjusting your search filters</p>
                        <button className="btn btn-success mt-3" onClick={handleAddJob}>
                            Post Your First Event
                        </button>
                    </div>
                )}

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <small className="text-muted">
                            Showing {(page - 1) * pageSize + 1} –{" "}
                            {Math.min(page * pageSize, filteredJobs.length)} of {filteredJobs.length}
                        </small>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                disabled={page === 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            <small className="text-muted">
                                Page {page} of {Math.max(1, totalPages)}
                            </small>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                disabled={page === totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </section>
    );
}

export default JfTotalApplications;